import { useCallback, useEffect, useRef } from 'react';
import getCookieValue from '~/utils/getCookieValue';
import { hashSha256 } from '~/utils/sha256';
import { generateUUID } from '~/utils/uuid';
import { useNonRevalidatingFetcher } from './useNonRevalidatingFetcher';

const buildEmailHashSha256 = (email?: string): string | undefined => {
  if (!email) return undefined;
  try {
    return hashSha256(email.toLowerCase());
  } catch {
    return undefined;
  }
};

// Sanitizes a URL by replacing jwt_token query parameter with a placeholder
const sanitizeUrl = (url: string): string => {
  try {
    const urlObj = new URL(url);
    if (urlObj.searchParams.has('jwt_token')) {
      urlObj.searchParams.set('jwt_token', 'REDACTED');
    }
    return urlObj.toString();
  } catch {
    return url;
  }
};

// Enum for analytics event types
enum AnalyticsEventType {
  PAGEVIEW = 'pageview',
  CLICK = 'click',
}

// Types for analytics events
interface AnalyticsEvent {
  user_agent: string;
  url: string;
  event: AnalyticsEventType;
  timestamp: number;
  event_id: string;
  nectar_id: string;
  publication_id: string;
  visit_token: string;
  ip: string;
  store_ip: boolean;
  click_type?: string;
  click_url?: string;
  click_raw_tag_name?: string;
  sendable_id?: string;
  sendable_type?: string;
  subscription_id?: number;
  email_hash_sha256?: string;
}

interface UseNectarAnalyticsProps {
  nectarId: string;
  enabled: boolean;
  publicationId: string;
  postId?: string;
  subscriptionId?: number;
  email?: string;
  settings: {
    batchSize: number;
    batchTimeoutMs: number;
    pageViewTracking: boolean;
    clickTracking: boolean;
    ipTracking: boolean;
  };
}

interface UseNectarAnalyticsReturn {
  trackPageView: () => void;
  trackClick: (clickData: { clickType: string; clickUrl?: string; clickRawTagName?: string }) => void;
}

// Hook for tracking analytics events
export const useNectarAnalytics = ({
  nectarId,
  enabled,
  publicationId,
  postId,
  subscriptionId,
  email,
  settings,
}: UseNectarAnalyticsProps): UseNectarAnalyticsReturn => {
  const lastPageViewRef = useRef<string>('');
  const eventQueue = useRef<AnalyticsEvent[]>([]);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isProcessingRef = useRef<boolean>(false);
  const fetcher = useNonRevalidatingFetcher();

  // Extract visit token from existing visit_token cookie
  const extractVisitToken = (): string | null => {
    if (typeof document === 'undefined') return null;

    return getCookieValue('visit_token', document.cookie);
  };

  // Send batch to server
  const sendBatch = useCallback(
    (events: AnalyticsEvent[]): void => {
      fetcher.submit(JSON.stringify(events), {
        method: 'post',
        action: '/nectar_analytics',
        encType: 'application/json',
      });
    },
    [fetcher],
  );

  // Process the event queue
  const processQueue = useCallback(() => {
    if (isProcessingRef.current || eventQueue.current.length === 0) {
      return;
    }

    isProcessingRef.current = true;

    // Take up to batchSize events from the queue
    const eventsToProcess = eventQueue.current.splice(0, settings.batchSize);

    if (eventsToProcess.length === 0) {
      isProcessingRef.current = false;
      return;
    }

    sendBatch(eventsToProcess);

    isProcessingRef.current = false;

    // Schedule next processing if there are more events
    if (eventQueue.current.length > 0) {
      // Schedule processing after timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => {
        processQueue();
      }, settings.batchTimeoutMs);
    }
  }, [settings.batchSize, settings.batchTimeoutMs, sendBatch]);

  // Schedule queue processing
  const scheduleProcessing = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      processQueue();
    }, settings.batchTimeoutMs);
  };

  // Force flush the queue (useful for page unload, etc.)
  const flushQueue = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    processQueue();
  }, [processQueue]);

  // Send event to analytics endpoint
  const sendEvent = (event: AnalyticsEvent): void => {
    eventQueue.current.push(event);

    // If we've reached the batch size, process immediately
    if (eventQueue.current.length >= settings.batchSize) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      processQueue();
    } else {
      // Otherwise, schedule processing after timeout
      scheduleProcessing();
    }
  };

  // Create base event with common properties
  const createBaseEvent = (eventType: AnalyticsEventType): AnalyticsEvent | null => {
    const visitToken = extractVisitToken();
    if (!visitToken) {
      return null;
    }

    const baseEvent: AnalyticsEvent = {
      user_agent: navigator.userAgent,
      url: sanitizeUrl(window.location.href),
      event: eventType,
      timestamp: Date.now(),
      event_id: generateUUID(),
      nectar_id: nectarId,
      publication_id: publicationId,
      visit_token: visitToken,
      ip: '', // Will be populated by server-side action
      store_ip: settings.ipTracking,
      sendable_id: postId,
      sendable_type: postId ? 'post' : undefined,
      subscription_id: subscriptionId,
      email_hash_sha256: buildEmailHashSha256(email),
    };

    return baseEvent;
  };

  // Track page view
  const trackPageView = (): void => {
    if (!settings.pageViewTracking) return;

    const currentUrl = window.location.href;

    // Don't track duplicate page views for the same URL
    if (currentUrl === lastPageViewRef.current) return;
    lastPageViewRef.current = currentUrl;

    const event = createBaseEvent(AnalyticsEventType.PAGEVIEW);
    if (!event) return;

    sendEvent(event);
  };

  // Track click event
  const trackClick = ({
    clickType,
    clickUrl,
    clickRawTagName,
  }: { clickType: string; clickUrl?: string; clickRawTagName?: string }): void => {
    if (!settings.clickTracking) return;

    const event = createBaseEvent(AnalyticsEventType.CLICK);
    if (!event) return;

    event.click_type = clickType;
    event.click_url = clickUrl;
    event.click_raw_tag_name = clickRawTagName;
    sendEvent(event);
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Flush queue on page unload
  useEffect(() => {
    const handleBeforeUnload = () => {
      flushQueue();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [flushQueue]);

  // Early return with no-op methods if analytics is disabled
  if (!enabled) {
    return {
      trackPageView: () => {},
      trackClick: () => {},
    };
  }

  return {
    trackPageView,
    trackClick,
  };
};

export type { AnalyticsEvent, UseNectarAnalyticsProps, UseNectarAnalyticsReturn };
